import { useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import { SvgCheveronDown, SvgCheveronRight } from '@/components/icons';
import { send } from '@/api/send';
import { Link } from '@/components/common/Link';
import { Page } from '@/components/layout/Page';
import { useAppStore } from '@/store/app';
import { usePref, useGlobalPref } from '@/store/prefs';
import { downloadCsv, transactionsToCsv } from '@/utils/csv';

function Setting({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2" data-testid={`setting-${title}`}>
      <h2 className="text-md font-semibold text-page-text">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-page-text">{label}</span>
      {children}
    </div>
  );
}

/** Port of `components/settings/index.tsx`, trimmed to what this clone wires. */
export function SettingsPage() {
  const [theme, setTheme] = useGlobalPref('theme');
  const [skin, setSkin] = useGlobalPref('skin');
  const [preferredDark, setPreferredDark] = useGlobalPref('preferredDarkTheme');
  const [currency, setCurrency] = usePref('currency');
  const [dateFormat, setDateFormat] = usePref('longDateFormat');
  const [budgetType] = usePref('budgetType');
  const [advanced, setAdvanced] = useState(false);
  const [lastAction, setLastAction] = useState('');
  const serverVersion = useAppStore(state => state.serverVersion);

  async function runUndo() {
    const result = await send('undo');
    setLastAction(
      result.didUndo ? `已撤销：${result.messages.join('、')}` : '没有可撤销的操作',
    );
    await Promise.all([
      useAppStore.getState().refreshAccounts(),
      useAppStore.getState().load(),
    ]);
  }

  async function runRedo() {
    const result = await send('redo');
    setLastAction(
      result.didRedo ? `已重做：${result.messages.join('、')}` : '没有可重做的操作',
    );
    await Promise.all([
      useAppStore.getState().refreshAccounts(),
      useAppStore.getState().load(),
    ]);
  }

  async function exportBudget() {
    const [transactions, state] = await Promise.all([
      send('transactions-get', {}),
      Promise.resolve(useAppStore.getState()),
    ]);
    const accountNames = new Map(
      state.accounts.map(account => [account.id, account.name]),
    );
    const categoryNames = new Map<string, string>();
    for (const group of state.categoryGroups) {
      for (const category of group.categories) {
        categoryNames.set(category.id, category.name);
      }
    }
    downloadCsv(
      'actual-budget-export',
      transactionsToCsv(transactions, { accountNames, categoryNames }),
    );
  }

  return (
    <Page title="设置">
      <div
        className="flex w-full max-w-[530px] flex-col gap-[30px] pb-8 pt-2"
        data-testid="settings-page"
      >
        <Setting title="关于">
          <p className="text-sm text-page-text">
            <strong>Actual</strong> 是一款快速、注重隐私的记账应用。
          </p>
          <p className="text-sm text-page-text-light">
            客户端版本：v26.9.0 · 服务器版本：v{serverVersion}
          </p>
          <p className="text-sm">
            <Link variant="external" to="https://actualbudget.org/docs/releases">
              发行说明
            </Link>
          </p>
        </Setting>

        <Setting title="主题">
          <Row label="视觉风格">
            <Select
              data-testid="setting-skin"
              value={skin ?? 'refined'}
              onChange={event => setSkin(event.target.value)}
              options={[
                { value: 'refined', label: '墨记（本复刻重做的版式）' },
                { value: 'clone', label: '复刻原样（上游 Actual 观感）' },
              ]}
            />
          </Row>
          <Row label="配色主题">
            <Select
              data-testid="setting-theme"
              value={theme ?? 'light'}
              onChange={event => setTheme(event.target.value)}
              options={[
                { value: 'light', label: '浅色' },
                { value: 'dark', label: '深色' },
                { value: 'midnight', label: '午夜蓝' },
                { value: 'auto', label: '跟随系统' },
              ]}
            />
          </Row>
          <Row label="深色时优先使用">
            <Select
              value={preferredDark ?? 'dark'}
              disabled={theme !== 'auto'}
              onChange={event => setPreferredDark(event.target.value)}
              options={[
                { value: 'dark', label: '深色' },
                { value: 'midnight', label: '午夜蓝' },
              ]}
            />
          </Row>
        </Setting>

        <Setting title="格式">
          <Row label="货币">
            <Select
              data-testid="setting-currency"
              value={currency ?? 'USD'}
              onChange={event => setCurrency(event.target.value)}
              options={[
                { value: 'CNY', label: '人民币 ¥' },
                { value: 'USD', label: '美元 $' },
                { value: 'EUR', label: '欧元 €' },
                { value: 'GBP', label: '英镑 £' },
                { value: 'JPY', label: '日元 ¥' },
              ]}
            />
          </Row>
          <Row label="日期格式">
            <Select
              data-testid="setting-date-format"
              value={dateFormat ?? 'yyyy-MM-dd'}
              onChange={event => setDateFormat(event.target.value)}
              options={[
                { value: 'yyyy-MM-dd', label: '2026-12-31' },
                { value: 'MM/dd/yyyy', label: '12/31/2026' },
                { value: 'dd/MM/yyyy', label: '31/12/2026' },
              ]}
            />
          </Row>
        </Setting>

        <Setting title="预算方式">
          <Row label="记账方法">
            <span className="text-sm text-page-text">
              {budgetType === 'tracking' ? '跟踪式' : '信封式'}
            </span>
          </Row>
          <p className="text-xs text-page-text-light">
            本复刻未实现跟踪式（Tracking）预算，预算表始终使用信封式布局。
          </p>
        </Setting>

        <Setting title="导出预算">
          <div>
            <Button size="sm" onClick={() => void exportBudget()}>
              导出全部交易
            </Button>
          </div>
          <p className="text-xs text-page-text-light">
            将模拟接口当前的全部交易下载为 CSV。
          </p>
        </Setting>

        <Setting title="高级">
          <button
            type="button"
            onClick={() => setAdvanced(value => !value)}
            data-testid="setting-advanced-toggle"
            className="flex items-center gap-1 text-sm text-page-text-link"
          >
            {advanced ? (
              <SvgCheveronDown width={12} height={12} />
            ) : (
              <SvgCheveronRight width={12} height={12} />
            )}
            {advanced ? '收起' : '展开'}高级设置
          </button>
          {advanced && (
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex gap-2">
                <Button size="sm" onClick={() => void runUndo()}>
                  撤销
                </Button>
                <Button size="sm" onClick={() => void runRedo()}>
                  重做
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    localStorage.removeItem('money404.mock.prefs');
                    window.location.reload();
                  }}
                >
                  重置演示数据
                </Button>
              </div>
              {lastAction && (
                <p className="text-xs text-page-text-light" data-testid="undo-result">
                  {lastAction}
                </p>
              )}
              <p className="text-xs text-page-text-light">
                撤销、重做和重置都作用于内存中的模拟数据库；重新加载页面会恢复种子数据。
              </p>
            </div>
          )}
        </Setting>
      </div>
    </Page>
  );
}
